<template>
    <div class="zoho-widget">
        <div class="widget-header">
            <h3>{{ $t("message.zohoCRM") }}</h3>
            <SupsisButton v-if="!hasZohoChannel" type="primary" @click="connectToZoho">{{ $t("message.connectToZoho") }}</SupsisButton>
            <SupsisButton v-else icon="open_in_new" ghost @click="gotoContactOnZoho">{{ $t("message.viewContact") }}</SupsisButton>
        </div>
        <a-alert v-if="hasZohoChannel" message="Connected to Zoho" type="success" />

        <ZohoDealsSection :contact="contact" @connect-zoho="connectToZoho" />
        <ZohoLeadsSection :contact="contact" />
        <ZohoPostOperationsSection v-if="postOperationSectionEnabled" :contact="contact" />
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import ZohoDealsSection from "./ZohoDealsSection.vue";
import ZohoLeadsSection from "./ZohoLeadsSection.vue";
import SupsisButton from "../../../../views/Pages/Settings/components/SupsisButton.vue";
import ZohoPostOperationsSection from "./ZohoPostOperationsSection.vue";
import DealNotes from "./DealNotes.vue";
import { ZOHO_API } from "../../../../api/zoho";
import CONFIG from "../../../../config/config";
import { socketMessageStream } from "../../../../utils/socketStream";
import { first, map, timeout } from "rxjs/operators";

export default Vue.extend({
    name: "ZohoWidget",
    components: {
        SupsisButton,
        ZohoDealsSection,
        ZohoLeadsSection,
        ZohoPostOperationsSection,
        DealNotes,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {};
    },
    computed: {
        ...mapState({
            postOperationSectionEnabled: (state) =>
                ["5d14ee322d95ad001ebdf213", "66ec460b2cac5e63c26dfad0"].includes(state.site.site._id),
            hasZohoChannel: (state) =>
                !!state.channel.channels.find((ch) => ch.type === "zoho" && ch.owner === state.auth.user._id),
            zohoChannelExists: (state) => !!state.channel.channels.find((x) => x.type === "zoho"),
            zohoChannel: (state) => state.channel.channels.find((x) => x.type === "zoho"),
            siteId: (state) => state.site.site._id,
        }),
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
        }),
        gotoContactOnZoho() {
            const orgId = _.get(this.zohoChannel, "payload.organization.domain_name");
            if (orgId && this.contact?.zohoInfo?.contactId) {
                window.open(
                    `https://crm.zoho.com/crm/${orgId}/tab/Contacts/${this.contact.zohoInfo.contactId}`,
                    "_blank"
                );
            } else if (orgId && this.contact?.zohoInfo?.leadId) {
                window.open(
                    `https://crm.zoho.com/crm/${orgId}/tab/Leads/${this.contact.zohoInfo.leadId}`,
                    "_blank"
                );
            } else {
                this.$message.error("Zoho CRM Contact/Lead Record Not Found.");
            }
        },
        async connectToZoho() {
            const CLIENT_ID = "1000.N2HT8IWMPR9WREGR8WUDIHOVAZ843A";
            const redirectUrl = this.getRedirectURL();
            const userId = this.$store.state.auth.user._id;
            const state = `${this.siteId}_${redirectUrl}_${userId}`;
            const scopes = ["ZohoCRM.modules.contacts.ALL", "ZohoCRM.modules.deals.ALL", "ZohoCRM.settings.ALL"];

            const url = `https://accounts.zoho.com/oauth/v2/auth?scope=${scopes.join(
                ","
            )}&client_id=${CLIENT_ID}&response_type=code&access_type=offline&redirect_uri=${redirectUrl}&state=${state}`;

            this.listenSocket();
            this.window = window.open(url, "_blank", "width=600,height=600");
        },
        listenSocket() {
            socketMessageStream
                .pipe(
                    first((x) => x.cmd === "zoho_callback_response"),
                    map((x) => x.data),
                    timeout(30000)
                )
                .toPromise()
                .then(async (data) => {
                    this.window?.close();
                    console.log("Zoho callback response", data, !data?.success);
                    const payload = data;
                    if (!data?.success) {
                        this.$message.error("Failed to connect Zoho CRM");
                        return;
                    }
                    await this.createZohoChannel(payload);
                    this.$message.success("Zoho CRM connected successfully");
                })
                .catch((err) => {
                    this.window?.close();
                    this.$message.error("Failed to connect Zoho CRM");
                });
        },
        async createZohoChannel(payload) {
            const data = {
                payload,
                type: "zoho",
                name: `${this.$store.state.auth.user.name}'s Zoho`,
                owner: this.$store.state.auth.user._id,
            };

            try {
                await this.createChannel(data);
                this.$message.success("Zoho channel created successfully");
            } catch (err) {
                this.$message.error("Failed to create Zoho channel");
            }
        },
        getRedirectURL() {
            return `${CONFIG.SOCKET_URL}/webhook/oauth2/zoho`;
        },
    },
    watch: {
        contact: {
            handler() {
                this.$store.dispatch("zoho/setSelectedDeals", []);
            },
            deep: true,
        },
    },
    beforeDestroy() {
        this.$store.dispatch("zoho/setSelectedDeals", []);
    },
});
</script>

<style scoped lang="scss">
.zoho-widget {
    padding: 10px;

    .widget-header {
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
        }
    }

    .view-contact-btn {
        padding: 6px 12px;
        background-color: #2c5282;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: #1e3a5c;
        }
    }

    .section-header {
        margin-bottom: 12px;

        h4 {
            font-size: 16px;
            font-weight: 500;
            color: #4a5568;
            margin: 0;
        }
    }

    .loading-container {
        min-height: 200px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        svg {
            width: 100px !important;
            height: 100px !important;
        }
    }

    .deals-section {
        .deal-row {
            display: flex;
            padding: 12px;
            border-bottom: 1px solid #eee;
            cursor: pointer;
            transition: background-color 0.2s;

            &:hover {
                background-color: #f5f5f5;
            }

            &.selected-row {
                background-color: #e6f0ff;
            }

            .deal-icon {
                margin-right: 12px;
                color: #2c5282;
                padding-top: 4px;
            }

            .deal-info {
                flex: 1;

                .deal-name,
                .deal-stage,
                .deal-footer > div,
                .deal-date {
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    i.material-icons {
                        font-size: 18px;
                        color: #64748b;
                    }
                }

                .deal-name {
                    font-weight: 500;
                    font-size: 15px;
                    color: #2d3748;
                    margin-bottom: 4px;
                }

                .deal-stage {
                    display: inline-flex;
                    background: #e2e8f0;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 0.85em;
                    color: #4a5568;
                    margin-bottom: 4px;
                }

                .deal-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.9em;
                    margin-bottom: 4px;

                    .deal-owner {
                        color: #718096;
                    }

                    .deal-amount {
                        font-weight: 500;
                        color: #2c5282;
                    }
                }

                .deal-date {
                    font-size: 0.85em;
                    color: #94a3b8;
                }
            }

            .deal-selection {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;

                .selection-circle {
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    border: 2px solid #ccc;
                    transition: background-color 0.2s, border-color 0.2s;

                    &.selected {
                        background-color: #2c5282;
                        border-color: #2c5282;
                    }
                }
            }
        }
    }

    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px;
    }

    .no-deals {
        text-align: center;
        padding: 20px;
        color: #666;
    }

    .empty-deals-container {
        min-height: 200px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 20px;
        color: #94a3b8;

        .empty-icon {
            font-size: 48px;
            margin-bottom: 12px;
        }

        .empty-text {
            font-size: 14px;
            text-align: center;
            margin: 0;
        }
    }
}
</style>
<style lang="scss">
.deal-viewer-modal {
    .ant-modal {
        width: 700px !important;
    }
}
</style>
